import { addWeeks, format } from 'date-fns';

/** Wrap width used for note text, preserved from the original height math. */
export const NOTES_WRAP_WIDTH = 180;

/**
 * Wrap width for the training name lines: the 150px Notes column minus the
 * 4px/2px cell padding applied in R0003CellStyle. Narrower than the note wrap
 * width above so long name lists are given enough room rather than clipping.
 */
export const NAMES_WRAP_WIDTH = 144;

/** Rendered line height of a training name line (.7rem at ~1.35 line-height). */
export const NAMES_LINE_HEIGHT = 15;

/**
 * Extra height taken by the divider above the names: 1px rule plus the 2px
 * margin and 2px padding around it. Only applies when a note sits above.
 */
export const NAMES_DIVIDER_HEIGHT = 5;

const NOTES_FONT = 'normal 12px verdana';
const NAMES_FONT = 'normal 12px verdana';

/** Rows whose content is a single line; every other row is two lines tall. */
const SINGLE_LINE_DAYS = ['Totals', 'Avg Wage', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun', ''];

export const filterData = (array, area) => {
  const idx = array.findIndex(x => x.area === area);
  return array[idx];
};

export const generateWeeks = (startWeek) => {
  const today = new Date();
  let week = new Date(startWeek);
  let weeks = [];

  while (week < today) {
    weeks.unshift(format(week, 'yyyy-MM-dd'));
    week = addWeeks(week, 1);
  }

  // Add two future weeks
  weeks.unshift(format(week, 'yyyy-MM-dd'));
  week = addWeeks(week, 1);
  weeks.unshift(format(week, 'yyyy-MM-dd'));
  return weeks;
};

export const getTextWidth = (text, font) => {
  const canvas = getTextWidth.canvas || (getTextWidth.canvas = document.createElement("canvas"));
  const context = canvas.getContext("2d");
  context.font = font;
  return context.measureText(text).width;
};

/**
 * Number of wrapped lines `text` occupies at the given width.
 *
 * @param {string} text - Text to measure; empty text occupies no lines
 * @param {Function} measureWidth - `(text) => number` pixel width measurer
 * @param {number} maxWidth - Wrap width in pixels
 * @returns {number} Line count (0 when there is nothing to render)
 */
export const countWrappedLines = (text, measureWidth, maxWidth) => {
  if (!text) return 0;
  if (text.length === 1) return 1;
  return Math.max(1, Math.ceil(measureWidth(text) / maxWidth));
};

/**
 * Builds the training name lines rendered beneath a day's note.
 * A group with no names produces an empty string and is not rendered.
 *
 * @param {Object} row - Report row, optionally carrying trainingNamesFOH/BOH
 * @returns {{fohText: string, bohText: string}} Rendered line text
 */
export const buildTrainingNameLines = (row) => {
  const foh = row?.trainingNamesFOH;
  const boh = row?.trainingNamesBOH;

  return {
    fohText: foh?.length ? `FOH: ${foh.join(', ')}` : '',
    bohText: boh?.length ? `BOH: ${boh.join(', ')}` : ''
  };
};

/**
 * Pixel height of a Notes cell: the wrapped note lines at the row's own line
 * height, plus any training name lines at their smaller line height.
 *
 * The measurers are injected so this stays testable - `getTextWidth` needs a
 * canvas, which jsdom does not provide.
 *
 * @param {Object} params
 * @param {string} params.notes - Note text for the row
 * @param {string} params.fohText - Rendered FOH name line, or ''
 * @param {string} params.bohText - Rendered BOH name line, or ''
 * @param {number} params.lineHeight - Height of one note line for this row
 * @param {Function} params.measureNotes - Width measurer for note text
 * @param {Function} params.measureNames - Width measurer for name text
 * @returns {number} Row height, never less than one line
 */
export const calculateNotesCellHeight = ({
  notes,
  fohText,
  bohText,
  lineHeight,
  measureNotes,
  measureNames
}) => {
  const noteLines = countWrappedLines(notes, measureNotes, NOTES_WRAP_WIDTH);
  const nameLines =
    countWrappedLines(fohText, measureNames, NAMES_WRAP_WIDTH) +
    countWrappedLines(bohText, measureNames, NAMES_WRAP_WIDTH);

  // The divider is only rendered when names follow a note.
  const dividerHeight = (noteLines > 0 && nameLines > 0) ? NAMES_DIVIDER_HEIGHT : 0;

  const height = (noteLines * lineHeight) + (nameLines * NAMES_LINE_HEIGHT) + dividerHeight;

  return Math.max(height, lineHeight);
};

export const getItemSize = (row) => {
  const { day, notes } = row;
  const charSize = Math.ceil(parseFloat(getComputedStyle(document.documentElement).fontSize));

  const lineHeight = SINGLE_LINE_DAYS.includes(day) ? charSize : charSize * 2;

  const { fohText, bohText } = buildTrainingNameLines(row);

  return calculateNotesCellHeight({
    notes,
    fohText,
    bohText,
    lineHeight,
    measureNotes: text => getTextWidth(text, NOTES_FONT),
    measureNames: text => getTextWidth(text, NAMES_FONT)
  });
};
